// FILE: /src/utils/socialConnectOrigin.js

const SOCIAL_CONNECT_ORIGIN_KEY =
  "oolook_social_connect_origin";

const SOCIAL_CONNECT_RESULT_KEY =
  "oolook_social_connect_result";

const SOCIAL_CONNECT_ORIGIN_TTL_MS =
  30 * 60 * 1000;

const isSafeInternalPath = (value) => {
  return Boolean(
    typeof value === "string" &&
      value.startsWith("/") &&
      !value.startsWith("//")
  );
};

const normalizePlatform = (
  value = ""
) => {
  return String(value || "")
    .trim()
    .toUpperCase();
};

const createConnectionResultId =
  () => {
    if (
      typeof crypto !==
        "undefined" &&
      typeof crypto.randomUUID ===
        "function"
    ) {
      return crypto.randomUUID();
    }

    return `connect_${Date.now()}_${Math.random()
      .toString(36)
      .slice(2)}`;
  };

const getConnectedAccountProfileId = (
  account = {}
) => {
  const platform =
    normalizePlatform(
      account.platform
    );

  if (platform === "FACEBOOK") {
    return account.platform_pageId
      ? String(
          account.platform_pageId
        )
      : null;
  }

  return account.platform_id
    ? String(account.platform_id)
    : null;
};

const getConnectedAccountKey = (
  account = {}
) => {
  const platform =
    normalizePlatform(
      account.platform
    );

  const profileId =
    getConnectedAccountProfileId(
      account
    );

  if (!platform || !profileId) {
    return null;
  }

  return `${platform}:${profileId}`;
};

const normalizeConnectedAccount = (
  account = {}
) => {
  const platform =
    normalizePlatform(
      account.platform
    );

  const profileId =
    getConnectedAccountProfileId(
      account
    );

  if (!platform || !profileId) {
    return null;
  }

  return {
    platform,
    profileId,
    platformId:
      account.platform_id
        ? String(
            account.platform_id
          )
        : null,
    platformPageId:
      account.platform_pageId
        ? String(
            account.platform_pageId
          )
        : null,
    connectionId:
      account.connectionId
        ? String(
            account.connectionId
          )
        : null,
    profileName:
      String(
        account.profile ||
          profileId
      ).trim(),
  };
};

const getGroupAccounts = ({
  groups = [],
  groupName = null,
} = {}) => {
  if (!Array.isArray(groups)) {
    return [];
  }

  const targetGroup =
    groups.find(
      (group) =>
        String(
          group?.name || ""
        ) ===
        String(
          groupName || ""
        )
    ) || null;

  return Array.isArray(
    targetGroup?.connected_accounts
  )
    ? targetGroup.connected_accounts
    : [];
};

export const clearSocialConnectOrigin =
  () => {
    sessionStorage.removeItem(
      SOCIAL_CONNECT_ORIGIN_KEY
    );
  };

export const clearSocialConnectResult =
  () => {
    sessionStorage.removeItem(
      SOCIAL_CONNECT_RESULT_KEY
    );
  };

export const setSocialConnectOrigin = ({
  source,
  sessionId = null,
  groupName = null,
  platform = null,
  requestedPlatforms = [],
  returnPath = null,
  connectedAccounts = [],
} = {}) => {
  if (source !== "copilot") {
    clearSocialConnectOrigin();
    clearSocialConnectResult();
    return;
  }

  const safeReturnPath =
    isSafeInternalPath(returnPath)
      ? returnPath
      : "/Dashboard";

  const beforeAccountKeys = [
    ...new Set(
      (
        Array.isArray(
          connectedAccounts
        )
          ? connectedAccounts
          : []
      )
        .map(
          getConnectedAccountKey
        )
        .filter(Boolean)
    ),
  ];

  sessionStorage.setItem(
    SOCIAL_CONNECT_ORIGIN_KEY,
    JSON.stringify({
      source: "copilot",
      sessionId:
        sessionId
          ? String(sessionId)
          : null,
      groupName:
        groupName || null,
      platform:
        platform || null,
      requestedPlatforms: [
        ...new Set(
          (
            Array.isArray(
              requestedPlatforms
            )
              ? requestedPlatforms
              : []
          )
            .map(normalizePlatform)
            .map((item) =>
              item ===
              "LINKEDIN_BUSINESS"
                ? "LINKEDIN_COMPANY"
                : item
            )
            .filter(Boolean)
        ),
      ],
      returnPath:
        safeReturnPath,
      beforeAccountKeys,
      createdAt:
        Date.now(),
    })
  );

  clearSocialConnectResult();
};

export const getSocialConnectOrigin =
  () => {
    const storedValue =
      sessionStorage.getItem(
        SOCIAL_CONNECT_ORIGIN_KEY
      );

    if (!storedValue) {
      return null;
    }

    try {
      const parsed =
        JSON.parse(storedValue);

      const createdAt =
        Number(
          parsed?.createdAt || 0
        );

      const expired =
        !createdAt ||
        Date.now() - createdAt >
          SOCIAL_CONNECT_ORIGIN_TTL_MS;

      if (
        expired ||
        parsed?.source !==
          "copilot" ||
        !isSafeInternalPath(
          parsed?.returnPath
        )
      ) {
        clearSocialConnectOrigin();
        return null;
      }

      return parsed;
    } catch (_) {
      clearSocialConnectOrigin();
      return null;
    }
  };

export const getSocialConnectReturnPath =
  (
    fallback =
      "/Dashboard/GroupsProfile"
  ) => {
    const origin =
      getSocialConnectOrigin();

    return origin?.returnPath ||
      fallback;
  };

export const recordSocialConnectResult = ({
  groups = [],
  fallbackPlatform = null,
} = {}) => {
  const origin =
    getSocialConnectOrigin();

  if (!origin) {
    return null;
  }

  const beforeAccountKeys =
    new Set(
      Array.isArray(
        origin.beforeAccountKeys
      )
        ? origin.beforeAccountKeys
        : []
    );

  const currentAccounts =
    getGroupAccounts({
      groups,
      groupName:
        origin.groupName,
    });

  const newlyConnectedAccounts =
    currentAccounts
      .filter((account) => {
        const key =
          getConnectedAccountKey(
            account
          );

        return (
          key &&
          !beforeAccountKeys.has(key)
        );
      })
      .map(
        normalizeConnectedAccount
      )
      .filter(Boolean);

  const completedPlatform =
    (() => {
      const value =
        normalizePlatform(
          origin.platform ||
            fallbackPlatform
        );

      return value ===
        "LINKEDIN_BUSINESS"
        ? "LINKEDIN_COMPANY"
        : value || null;
    })();

  const requestedPlatforms = [
    ...new Set(
      (
        Array.isArray(
          origin.requestedPlatforms
        )
          ? origin.requestedPlatforms
          : []
      )
        .map(normalizePlatform)
        .map((item) =>
          item ===
          "LINKEDIN_BUSINESS"
            ? "LINKEDIN_COMPANY"
            : item
        )
        .filter(Boolean)
    ),
  ];

  const remainingPlatforms =
    requestedPlatforms.filter(
      (item) =>
        item !== completedPlatform
    );

  const result = {
    resultId:
      createConnectionResultId(),
    source: "copilot",
    sessionId:
      origin.sessionId || null,
    groupName:
      origin.groupName || null,
    platform:
      completedPlatform,
    requestedPlatforms,
    remainingPlatforms,
    accounts:
      newlyConnectedAccounts,
    connectedCount:
      newlyConnectedAccounts.length,
    completedAt:
      Date.now(),
  };

  sessionStorage.setItem(
    SOCIAL_CONNECT_RESULT_KEY,
    JSON.stringify(result)
  );

  return result;
};

export const getSocialConnectResult =
  () => {
    const storedValue =
      sessionStorage.getItem(
        SOCIAL_CONNECT_RESULT_KEY
      );

    if (!storedValue) {
      return null;
    }

    try {
      const parsed =
        JSON.parse(storedValue);

      const completedAt =
        Number(
          parsed?.completedAt || 0
        );

      if (
        !completedAt ||
        Date.now() - completedAt >
          SOCIAL_CONNECT_ORIGIN_TTL_MS
      ) {
        clearSocialConnectResult();
        return null;
      }

      return parsed;
    } catch (_) {
      clearSocialConnectResult();
      return null;
    }
  };

export const consumeSocialConnectResult =
  () => {
    const result =
      getSocialConnectResult();

    if (result) {
      clearSocialConnectResult();
    }

    return result;
  };


  
export const returnFromSocialConnectWithoutResult =
  (
    navigate,
    fallback =
      "/Dashboard/GroupsProfile"
  ) => {
    const returnPath =
      getSocialConnectReturnPath(
        fallback
      );

    clearSocialConnectResult();
    clearSocialConnectOrigin();

    navigate(returnPath, {
      replace: true,
    });
  };

export const finishSocialConnectNavigation =
  (
    navigate,
    fallback =
      "/Dashboard/GroupsProfile",
    {
      groups = [],
      platform = null,
    } = {}
  ) => {
    const returnPath =
      getSocialConnectReturnPath(
        fallback
      );

    recordSocialConnectResult({
      groups,
      fallbackPlatform:
        platform,
    });

    clearSocialConnectOrigin();

    navigate(returnPath, {
      replace: true,
    });
  };